import React, { useState, useEffect } from 'react';
import { Instagram, Linkedin, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

const formatTimeAgo = (dateString, locale) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = Math.max(0, now - date);
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (locale === 'en') {
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
    if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return diffMins === 1 ? 'Vor 1 Minute' : `Vor ${diffMins} Minuten`;
    if (diffHours < 24) return diffHours === 1 ? 'Vor 1 Stunde' : `Vor ${diffHours} Stunden`;
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Vor 1 Tag';
    if (diffDays < 7) return `Vor ${diffDays} Tagen`;
    if (diffDays < 14) return 'Vor 1 Woche';
    if (diffDays < 30) return `Vor ${Math.floor(diffDays / 7)} Wochen`;
    return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  }
};

const InstagramPost = ({ post, locale }) => {
  const { t } = useLanguage();

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100/80 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] flex items-center justify-center shrink-0">
          <Instagram className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="font-bold text-gray-800 text-xs">@nextgenbusinesscup</p>
          <p className="text-[10px] text-gray-400">{formatTimeAgo(post.timestamp, locale)}</p>
        </div>
      </div>
      {post.videoUrl ? (
        <div
          className="overflow-hidden bg-black"
          style={{ aspectRatio: '9 / 16' }}
        >
          <img
            src={post.posterUrl || post.imageUrl}
            alt={post.caption?.slice(0, 50) || 'Instagram post'}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      ) : (
        post.imageUrl && (
          <div className="bg-gray-100 overflow-hidden flex justify-center">
            <img
              src={post.imageUrl}
              alt={post.caption?.slice(0, 50) || 'Instagram post'}
              className="w-full object-contain max-h-[min(520px,65vh)] bg-gray-100"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
              }}
            />
          </div>
        )
      )}
      {post.caption && (
        <div className="p-3">
          <p className="text-gray-600 text-xs leading-relaxed whitespace-pre-line max-h-40 overflow-y-auto">
            {post.caption}
          </p>
        </div>
      )}
      <div className="px-3 pb-3">
        <a
          href={post.permalink || 'https://www.instagram.com/nextgenbusinesscup/'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-[#E1306C] hover:text-[#c13584] font-medium transition-colors inline-flex items-center gap-1"
        >
          {t('home.socialWallInstagramViewAll')}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

const LinkedInPost = ({ post, locale }) => {
  const { t } = useLanguage();

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-100/80 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
        <div className="w-11 h-11 bg-[#0077b5] rounded-full flex items-center justify-center shrink-0">
          <Linkedin className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-bold text-gray-800 text-sm">Next Gen <span className="italic font-normal">Business</span> Cup</p>
          <p className="text-xs text-gray-400">{formatTimeAgo(post.timestamp, locale)}</p>
        </div>
      </div>
      <div className="p-5">
      {post.videoUrl ? (
          <div className="mb-3 rounded-xl overflow-hidden bg-black">
            <img
              src={post.posterUrl}
              alt="LinkedIn post image"
              className="w-full object-cover"
              style={{ aspectRatio: '16 / 9', maxHeight: '500px' }}
              loading="lazy"
            />
          </div>
        ) : post.imageUrl && (
          <div className="mb-3 rounded-xl overflow-hidden">
            <img
              src={post.imageUrl}
              alt="LinkedIn post image"
              className="w-full object-cover"
              style={{ aspectRatio: '16 / 9', maxHeight: '500px' }}
              loading="lazy"
            />
          </div>
        )}
        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
          {post.text}
        </p>
        {post.likes !== undefined && (
          <div className="mt-3 pt-2 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-400">
            <span>👍 {post.likes}</span>
            <span>💬 {post.comments}</span>
          </div>
        )}
      </div>
      <div className="px-5 pb-4">
        <a
          href={post.articleUrl || 'https://www.linkedin.com/company/next-gen-business-cup/posts'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs sm:text-sm text-[#0077b5] hover:text-[#005582] font-medium transition-colors inline-flex items-center gap-1"
        >
          {t('home.socialWallLinkedInViewAll')}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};

const TikTokPost = ({ post, locale }) => {
  const { t } = useLanguage();

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100/80 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
          </svg>
        </div>
        <div>
          <p className="font-bold text-gray-800 text-xs">@nextgenbusinesscup</p>
          <p className="text-[10px] text-gray-400">{formatTimeAgo(post.timestamp, locale)}</p>
        </div>
      </div>
      {post.videoUrl ? (
        <div
          className="overflow-hidden bg-black"
          style={{ aspectRatio: '9 / 16' }}
        >
          <img
            src={post.posterUrl || post.imageUrl}
            alt={post.caption?.slice(0, 50) || 'TikTok post'}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      ) : (
        post.imageUrl && (
          <div className="bg-gray-100 overflow-hidden flex justify-center">
            <img
              src={post.imageUrl}
              alt={post.caption?.slice(0, 50) || 'TikTok post'}
              className="w-full object-contain max-h-[min(520px,65vh)] bg-gray-100"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )
      )}
      {post.caption && (
        <div className="p-3">
          <p className="text-gray-600 text-xs leading-relaxed whitespace-pre-line line-clamp-3">
            {post.caption}
          </p>
        </div>
      )}
      <div className="px-3 pb-3">
        <a
          href={post.permalink || 'https://www.tiktok.com/@nextgenbusinesscup'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-gray-400 hover:text-black font-medium transition-colors inline-flex items-center gap-1"
        >
          {t('home.socialWallTikTokViewAll')}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export const SocialFeed = () => {
  const [instagramPosts, setInstagramPosts] = useState(null);
  const [linkedinPosts, setLinkedinPosts] = useState(null);
  const [tiktokPosts, setTiktokPosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { locale, t } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    let retryTimeout;

    const fetchPosts = async ({ silent = false } = {}) => {
      if (cancelled) return;
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        let data = null;

        try {
          const response = await axios.get(`${API_URL}/social/posts`, {
            timeout: 3000,
          });
          if (response.data) data = response.data;
        } catch (_) {}

        if (!data && !cancelled) {
          try {
            const localResponse = await fetch('/social-posts.json', { cache: 'no-store' });
            if (localResponse.ok) {
              data = await localResponse.json();
            }
          } catch (_) {}
        }

        if (data && !cancelled) {
          setInstagramPosts(data.instagram?.slice(0, 2) || []);
          setLinkedinPosts(data.linkedin?.slice(0, 2) || []);
          setTiktokPosts(data.tiktok?.slice(0, 2) || []);
          setLoading(false);
        } else if (!cancelled) {
          retryTimeout = setTimeout(() => fetchPosts({ silent: true }), 2000);
        }
      } catch (err) {
        if (!cancelled) {
          retryTimeout = setTimeout(() => fetchPosts({ silent: true }), 2000);
        }
      }
    };

    fetchPosts();

    const interval = setInterval(() => {
      if (!cancelled) fetchPosts({ silent: true });
    }, 30000);

    return () => {
      cancelled = true;
      clearTimeout(retryTimeout);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-ngbc-green animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!instagramPosts?.length && !linkedinPosts?.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">{t('home.socialWallPostsPlaceholder')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* LinkedIn Posts */}
      {linkedinPosts.map((post) => (
        <LinkedInPost key={post.id} post={post} locale={locale} />
      ))}

      {/* Instagram Posts - 2 column grid */}
      <div className="grid grid-cols-2 gap-3">
        {instagramPosts.map((post) => (
          <InstagramPost key={post.id} post={post} locale={locale} />
        ))}
      </div>

      {/* TikTok Posts - 2 column grid */}
      {tiktokPosts.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {tiktokPosts.map((post) => (
            <TikTokPost key={post.id} post={post} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialFeed;
